import { inject } from "fw";
import { handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { once } from "helpers/once";
import { GridStore, FetchListAction } from "./components/grid";
import { CommonAppIntegrationRepository } from "network/common-app-transfer-history-repository";
import { CommonAppTransferFilter, CommonAppSdsTransferHistoryInfo } from "models/common-app-transfers";

const AGGREGATIONS = "terms:status";
const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "atsOrganizationId")
    .set("status", "status")
    .set("commonAppYear", "commonAppYear")
    .set("range", "metaData.dateCreatedUtc");

const CACHE_KEY = "monitor-common-app-transfer-store";

export class ImportsEnsureAction {}
export class CommonAppEnsureAction {}
export class CommonAppRefreshAction {}
export class CommonAppNextPageAction {}
export class CommonAppPreviousPageAction {}
export class SelectedFilterAction {
    constructor(
        public key: number,
        public data: any
    ) {}
}

export class CommonAppFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}

export class CommonAppCollapseItemAction {
    constructor(public id: string) {}
}

@inject
export class MonitorCommonAppTransfersStore extends GridStore<CommonAppSdsTransferHistoryInfo, CommonAppTransferFilter> {
    private dataLoading: boolean = false;

    constructor(private network: CommonAppIntegrationRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private handleActionStart(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(CommonAppCollapseItemAction)
    private collapseItem(action: CommonAppCollapseItemAction) {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id
        }));
    }

    @handle(SelectedFilterAction)
    private handleSelectedFilterAction(action: SelectedFilterAction) {
        this.handleSelectedFilter(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(CommonAppRefreshAction)
    private refreshAction(action: CommonAppRefreshAction) {
        this.handleRefreshAction(action, () => {
            this.loadPageList();
        });
    }

    @handle(CommonAppFetchListAction)
    private fetchListAction(action: FetchListAction<CommonAppTransferFilter>) {
        this.handleFetchList(action, () => {
            this.loadPageList();
        });
    }

    @handle(CommonAppEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once(CACHE_KEY, async () => {
            if (this.state.loaded) return;
            this.restoreState();
            this.loadPageList();
        });
    }

    @handle(CommonAppNextPageAction)
    private nextPageAction(action: CommonAppNextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadPageData(null, nextPageToken);
        });
    }

    @handle(CommonAppPreviousPageAction)
    private previousPageAction(action: CommonAppPreviousPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadPageData(previousPageToken, null);
        });
    }

    private async loadPageList() {
        await this.loadPageData(this.state?.currentPreviousPageToken, this.state?.currentNextPageToken);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        if (this.dataLoading) return;
        this.dataLoading = true;
        const { pageSize, sort, search, filter, statusCounts, filterCounter } = this.state;
        const filterLength = filter ? Object.keys(filter).length : 0;
        let newStatusCounts = [];

        try {
            const clientModelResponse = await this.network.commonAppTransferHistory(
                this.getWholeFilter(true, FILTER_FIELDS_MAPPING),
                search,
                sort,
                previousPageToken,
                nextPageToken,
                null,
                AGGREGATIONS,
                false,
                pageSize
            );

            newStatusCounts = clientModelResponse.Aggregations["terms_status"]?.Items;

            if (typeof filter.status === "number" && filterLength === 1) newStatusCounts = statusCounts;

            if (typeof filter.status === "number" && filterLength > 1) {
                const clientModelResponseExceptStatus = await this.network.commonAppTransferHistory(
                    this.getWholeFilter(false, FILTER_FIELDS_MAPPING),
                    search,
                    sort,
                    previousPageToken,
                    nextPageToken,
                    null,
                    AGGREGATIONS,
                    true,
                    pageSize
                );
                newStatusCounts = clientModelResponseExceptStatus.Aggregations["terms_status"]?.Items;
            }

            this.setState((state) => ({
                ...state,
                loaded: true,
                filterCounter: filterLength,
                statusCounts: newStatusCounts ?? [],
                previousPageToken: clientModelResponse.PreviousPageToken,
                nextPageToken: clientModelResponse.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: clientModelResponse.Results,
                total: clientModelResponse.Total,
                updatedDate: new Date()
            }));
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        } finally {
            this.dataLoading = false;
        }
    }
}
